/**
 * Chores — rotation, materialisation, streaks, grace, swaps, escalation and
 * fairness. Implements docs/01 §3.
 *
 * Three properties hold for every function in this file, and they are the whole
 * point of the module:
 *
 *  1. **Pure.** No I/O, no globals, no `Date.now()`, no mutation of inputs.
 *     Every function that needs the time takes an explicit `now: number` (UTC
 *     epoch ms) or a `DayKey` computed from a `TrustedClock`.
 *
 *  2. **Device-independent.** Two tablets, both offline, both holding the same
 *     rows, must reach byte-identical answers with no coordination. That is why
 *     rotation is an arithmetic function of the date rather than a stored
 *     counter, why materialised instance ids default to a natural key, and why
 *     the swap race has a deterministic tie-break.
 *
 *  3. **Derived, not stored.** Streaks, escalation levels and fairness are
 *     recomputed from the instance rows every time. There is no counter to drift
 *     and nothing to repair after an offline period.
 *
 * All dates are `DayKey`s in the office's 05:00-rollover reckoning and all week
 * arithmetic goes through `time.ts` (`weekOf`, Saturday-start).
 */

import type { ChoreDefId, ChoreInstanceId, MemberId, SwapId } from './ids.js'
import type {
  ChoreDefinition,
  ChoreInstance,
  ChoreStatus,
  ChoreSwap,
  FairnessStat,
  StreakState,
} from './entities/chores.js'
import type { RamadanPeriod } from './entities/misc.js'
import type { DayKey, WeekKey } from './time.js'
import { WEEK_START_DOW, addDays, dayOfWeek, daysBetween, weekOf } from './time.js'

// ---------------------------------------------------------------------------
// Small internal helpers
// ---------------------------------------------------------------------------

/** Euclidean modulo: result is always in [0, m). `-1 % 3` is -1 in JS; this is 2. */
const floorMod = (n: number, m: number): number => ((n % m) + m) % m

/** Normalise any integer to a JS weekday, 0=Sun … 6=Sat. */
const norm7 = (n: number): number => floorMod(Math.trunc(n), 7)

/** Chronological comparator for DayKeys. Never relies on string ordering. */
const compareDays = (a: DayKey, b: DayKey): number => daysBetween(b, a)

/** A WeekKey is the DayKey of its Saturday; this crosses the brand deliberately. */
const weekStartDay = (w: WeekKey): DayKey => w as unknown as DayKey

/** True when `day` falls in the closed interval [from, to]. */
const inRange = (day: DayKey, from: DayKey, to: DayKey): boolean =>
  daysBetween(from, day) >= 0 && daysBetween(day, to) >= 0

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * The fixed epoch every rotation counts from (docs/01 §3.1). It is a Saturday,
 * so week indices line up with `weekOf` and occurrence 0 of a weekly chore is
 * the first office week of 2024.
 *
 * This value is part of the wire contract: changing it re-shuffles every future
 * assignment on every device. Do not change it without re-basing `rotationOffset`
 * on every definition.
 */
export const ROTATION_EPOCH_DAY = '2024-01-06' as DayKey

/** Instances are materialised this many days ahead so the tablet works offline. */
export const MATERIALISATION_HORIZON_DAYS = 14

// ---------------------------------------------------------------------------
// 1. Deterministic rotation
// ---------------------------------------------------------------------------

/**
 * Which weekday a weekly chore falls on, 0=Sun … 6=Sat.
 * A weekly definition with a null weekday is anchored to the start of the office
 * week (Saturday) rather than being silently skipped; out-of-range values wrap.
 */
export function weekdayOfWeeklyChore(def: Pick<ChoreDefinition, 'weekday'>): number {
  return norm7(def.weekday ?? WEEK_START_DOW)
}

/**
 * Whole cadence periods elapsed between the epoch and `dueDay`.
 *
 * Daily chores count days; weekly chores count *weeks* via `weekOf`, so every
 * day inside one office week shares an index and consecutive occurrences of the
 * chore differ by exactly one. Negative before the epoch, which is fine — the
 * modulo below is Euclidean.
 */
export function occurrenceIndex(
  def: Pick<ChoreDefinition, 'cadence'>,
  dueDay: DayKey,
  epochDay: DayKey = ROTATION_EPOCH_DAY,
): number {
  if (def.cadence === 'weekly') {
    return Math.round(daysBetween(weekStartDay(weekOf(epochDay)), weekStartDay(weekOf(dueDay))) / 7)
  }
  return daysBetween(epochDay, dueDay)
}

/**
 * Whose turn it is. A pure function of the definition and the date — no stored
 * counter, so two offline devices always agree (docs/01 §3.1).
 *
 *   assignee = rotationPool[(occurrenceIndex + rotationOffset) mod pool.length]
 *
 * Degenerate cases, all deliberate:
 *  - rotation disabled  → the fixed assignee (may be null)
 *  - empty pool         → falls back to the fixed assignee, else null
 *  - single-member pool → that member on every occurrence
 *
 * Changing the pool changes future answers, but never past ones that were
 * already materialised: history lives in the stored instances, and
 * `generateInstances` never rewrites an instance it did not create.
 */
export function assigneeFor(
  def: ChoreDefinition,
  dueDay: DayKey,
  epochDay: DayKey = ROTATION_EPOCH_DAY,
): MemberId | null {
  if (!def.rotationEnabled) return def.fixedAssigneeId
  const pool = def.rotationPool
  if (pool.length === 0) return def.fixedAssigneeId
  const idx = floorMod(
    occurrenceIndex(def, dueDay, epochDay) + Math.trunc(def.rotationOffset),
    pool.length,
  )
  return pool[idx] ?? def.fixedAssigneeId
}

// ---------------------------------------------------------------------------
// 2. Instance materialisation
// ---------------------------------------------------------------------------

/** True when any Ramadan period covers `day` (inclusive of both endpoints). */
export function isRamadanDay(day: DayKey, periods: readonly RamadanPeriod[] = []): boolean {
  return periods.some(p => inRange(day, p.startDay, p.endDay))
}

/** Does this definition have an occurrence due on `day`? */
export function occursOn(
  def: ChoreDefinition,
  day: DayKey,
  ramadanPeriods: readonly RamadanPeriod[] = [],
): boolean {
  if (!def.active) return false
  if (def.cadence === 'weekly' && dayOfWeek(day) !== weekdayOfWeeklyChore(def)) return false
  if (def.pausedInRamadan && isRamadanDay(day, ramadanPeriods)) return false
  return true
}

/** Every due day for a definition in the closed range [fromDay, toDay]. */
export function dueDaysBetween(
  def: ChoreDefinition,
  fromDay: DayKey,
  toDay: DayKey,
  ramadanPeriods: readonly RamadanPeriod[] = [],
): DayKey[] {
  const span = daysBetween(fromDay, toDay)
  if (span < 0) return []
  const out: DayKey[] = []
  for (let i = 0; i <= span; i++) {
    const day = addDays(fromDay, i)
    if (occursOn(def, day, ramadanPeriods)) out.push(day)
  }
  return out
}

/** The natural key of an instance: one occurrence per (definition, due day). */
export const instanceKey = (definitionId: ChoreDefId, dueDay: DayKey): string =>
  `${definitionId} ${dueDay}`

/**
 * Default instance id: deterministic, so two offline devices materialising the
 * same window mint the *same* row and the server's natural-key
 * `ON CONFLICT DO NOTHING` (docs/02 §8) collapses them instead of duplicating.
 *
 * `ids.ts` says ids are UUIDv7, but a UUIDv7 needs randomness and a clock, which
 * would make materialisation non-deterministic across devices. Callers who want
 * UUIDv7 anyway can inject their own factory — the server dedupes on the natural
 * key either way.
 */
export const naturalInstanceId = (definitionId: ChoreDefId, dueDay: DayKey): ChoreInstanceId =>
  `${definitionId}:${dueDay}` as ChoreInstanceId

export interface MaterialisationOptions {
  readonly ramadanPeriods?: readonly RamadanPeriod[] | undefined
  readonly instanceId?: ((definitionId: ChoreDefId, dueDay: DayKey) => ChoreInstanceId) | undefined
  readonly epochDay?: DayKey | undefined
}

/** The rolling window the tablet keeps materialised: [today, today + horizon]. */
export function materialisationWindow(
  today: DayKey,
  horizonDays: number = MATERIALISATION_HORIZON_DAYS,
): { fromDay: DayKey; toDay: DayKey } {
  return { fromDay: today, toDay: addDays(today, Math.max(0, Math.trunc(horizonDays))) }
}

/** Deterministic order: due day, then definition, then id. */
function sortInstances(list: ChoreInstance[]): ChoreInstance[] {
  return list.sort(
    (a, b) =>
      compareDays(a.dueDay, b.dueDay) ||
      (a.definitionId < b.definitionId ? -1 : a.definitionId > b.definitionId ? 1 : 0) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  )
}

/**
 * Materialise every missing occurrence in [fromDay, toDay] and return the union
 * with `existing`, sorted.
 *
 * Idempotent: an instance already present for a (definition, due day) is
 * returned untouched — same object, same assignee, same status — so re-running
 * after a pool change does not retroactively reshuffle history, and re-running
 * after a sync does not duplicate.
 *
 * Occurrences with no assignee (rotation enabled, empty pool, no fixed
 * assignee) are skipped rather than materialised unassigned.
 *
 * **Pool changes are a two-step job.** Because this function never rewrites a
 * row, an already-materialised future instance keeps the assignee it was minted
 * with — so on its own it would leave a new pool unapplied for up to a whole
 * horizon. docs/01 §3.1 wants the new pool live "from the next occurrence
 * onward", which is this composition, and it is what the rolling job must run:
 *
 * ```ts
 * const { fromDay, toDay } = materialisationWindow(today)
 * const grown = generateInstances(defs, fromDay, toDay, stored, opts)
 * const rows  = reassignPendingFrom(defs, grown, addDays(today, 1))
 * ```
 *
 * The cutoff is tomorrow, not today: history and the chore someone is already
 * holding today are never moved, everything from the next occurrence on is.
 */
export function generateInstances(
  defs: readonly ChoreDefinition[],
  fromDay: DayKey,
  toDay: DayKey,
  existing: readonly ChoreInstance[] = [],
  opts: MaterialisationOptions = {},
): ChoreInstance[] {
  const mkId = opts.instanceId ?? naturalInstanceId
  const ramadan = opts.ramadanPeriods ?? []
  const epoch = opts.epochDay ?? ROTATION_EPOCH_DAY

  const seen = new Set<string>()
  for (const inst of existing) seen.add(instanceKey(inst.definitionId, inst.dueDay))

  const out: ChoreInstance[] = existing.slice()
  for (const def of defs) {
    for (const day of dueDaysBetween(def, fromDay, toDay, ramadan)) {
      const key = instanceKey(def.id, day)
      if (seen.has(key)) continue
      const assignee = assigneeFor(def, day, epoch)
      if (assignee === null) continue
      seen.add(key)
      out.push({
        id: mkId(def.id, day),
        definitionId: def.id,
        dueDay: day,
        assigneeId: assignee,
        status: 'pending',
        completedAt: null,
        completedBy: null,
        escalationLevel: 0,
      })
    }
  }
  return sortInstances(out)
}

/**
 * Apply a changed rotation pool from `fromDay` onward (docs/01 §3.1: "history is
 * left alone and the new pool applies from the next occurrence onward").
 *
 * Only *pending* instances due on or after `fromDay` are re-assigned. Anything
 * already done, missed, graced or swapped keeps its assignee, as does every
 * instance in the past — an intern joining the rota must not rewrite who was
 * responsible last Tuesday.
 */
export function reassignPendingFrom(
  defs: readonly ChoreDefinition[],
  instances: readonly ChoreInstance[],
  fromDay: DayKey,
  opts: Pick<MaterialisationOptions, 'epochDay'> = {},
): ChoreInstance[] {
  const epoch = opts.epochDay ?? ROTATION_EPOCH_DAY
  const byId = new Map<ChoreDefId, ChoreDefinition>()
  for (const def of defs) byId.set(def.id, def)
  return instances.map(inst => {
    if (inst.status !== 'pending') return inst
    if (daysBetween(fromDay, inst.dueDay) < 0) return inst
    const def = byId.get(inst.definitionId)
    if (def === undefined) return inst
    const assignee = assigneeFor(def, inst.dueDay, epoch)
    if (assignee === null || assignee === inst.assigneeId) return inst
    return { ...inst, assigneeId: assignee }
  })
}

// ---------------------------------------------------------------------------
// Completion
// ---------------------------------------------------------------------------

/**
 * Mark an instance done. Idempotent: replaying the same completion — or any
 * completion of an already-done instance — returns the instance unchanged, so
 * a doubly-delivered outbox mutation changes nothing.
 *
 * Late completion of a missed or graced instance is allowed on purpose: an
 * escalating chore must be clearable, and this system is designed to encourage
 * finishing rather than to punish. Because the streak is derived from status,
 * a late completion also repairs that day.
 */
export function completeChore(
  instance: ChoreInstance,
  byMemberId: MemberId,
  now: number,
): ChoreInstance {
  if (instance.status === 'done') return instance
  return {
    ...instance,
    status: 'done',
    completedAt: now,
    completedBy: byMemberId,
    escalationLevel: 0,
  }
}

// ---------------------------------------------------------------------------
// 3. Streaks (docs/01 §3.3)
// ---------------------------------------------------------------------------

export interface StreakSettings {
  /** 0=Sun … 6=Sat. Default 6 (Saturday) — the Egyptian working week. */
  readonly weekStartDow: number
  /** Grace days forgiven per office week. Default 1. Never banked. */
  readonly graceDaysPerWeek?: number | undefined
}

export const DEFAULT_STREAK_SETTINGS: Required<StreakSettings> = {
  weekStartDow: WEEK_START_DOW,
  graceDaysPerWeek: 1,
}

/**
 * The office week containing `day`. Delegates to `time.ts`'s `weekOf` for the
 * default Saturday-start week; the parameter exists only because docs/01 §3.3
 * says the week start is configurable.
 */
export function officeWeekOf(day: DayKey, weekStartDow: number = WEEK_START_DOW): WeekKey {
  const start = norm7(weekStartDow)
  if (start === WEEK_START_DOW) return weekOf(day)
  return addDays(day, -floorMod(dayOfWeek(day) - start, 7)) as unknown as WeekKey
}

/**
 * How a day counts toward a member's streak.
 *  - `qualifying` — at least one chore due and *all* of them done. Extends.
 *  - `neutral`    — nothing due, or the day is still in progress. Neither.
 *  - `forgiven`   — a miss covered by the week's grace day. Survives.
 *  - `broken`     — a miss with no grace left. Resets.
 */
export type DayOutcome = 'qualifying' | 'neutral' | 'forgiven' | 'broken'

export interface StreakDay {
  readonly day: DayKey
  readonly week: WeekKey
  readonly outcome: DayOutcome
  readonly due: number
  readonly done: number
}

/**
 * Classify every day on which `memberId` had a chore due, up to and including
 * `upToDay`. Days with nothing due never appear — they are neutral by omission,
 * which is what stops a weekly-only member accruing a nonsense streak.
 *
 * Grace is derived here rather than read from stored statuses, so it is
 * authoritative and identical on every device: within each office week the
 * first `graceDaysPerWeek` miss days are forgiven in chronological order, the
 * rest break. An instance already stored as `graced` is still counted as the
 * miss it records, so deriving and reading agree.
 *
 * `upToDay` itself is always still in progress: unless every chore due on it is
 * already done, it is `neutral`. It cannot spend the week's grace and it cannot
 * break a run, whatever status its rows currently carry — the office day is not
 * over until it rolls over (docs/01, the day-boundary rule), `settleOverdue`
 * correspondingly refuses to settle it, and a late completion can still turn it
 * into a qualifying day.
 */
export function streakDays(
  memberId: MemberId,
  instances: readonly ChoreInstance[],
  upToDay: DayKey,
  settings: StreakSettings = DEFAULT_STREAK_SETTINGS,
): StreakDay[] {
  const grace = Math.max(0, Math.trunc(settings.graceDaysPerWeek ?? 1))
  const byDay = new Map<DayKey, ChoreInstance[]>()
  for (const inst of instances) {
    if (inst.assigneeId !== memberId) continue
    if (daysBetween(inst.dueDay, upToDay) < 0) continue // due after the horizon
    const list = byDay.get(inst.dueDay)
    if (list === undefined) byDay.set(inst.dueDay, [inst])
    else list.push(inst)
  }

  const days = [...byDay.keys()].sort(compareDays)
  const usedByWeek = new Map<WeekKey, number>()
  const out: StreakDay[] = []

  for (const day of days) {
    const list = byDay.get(day) ?? []
    const due = list.length
    let done = 0
    for (const inst of list) {
      if (inst.status === 'done') done++
    }
    const week = officeWeekOf(day, settings.weekStartDow)

    let outcome: DayOutcome
    if (done === due) {
      outcome = 'qualifying'
    } else if (day === upToDay) {
      // Today is not over. Nothing inside it can decide the streak — not even a
      // `missed` or `graced` row someone else's device already wrote, because
      // `settleOverdue` never settles today and a late completion can still
      // rescue the day. Judging today off such a row made the two functions
      // disagree about whether today was decided, and let a skewed clock on one
      // device break a streak that was still perfectly winnable.
      outcome = 'neutral'
    } else {
      const used = usedByWeek.get(week) ?? 0
      if (used < grace) {
        usedByWeek.set(week, used + 1)
        outcome = 'forgiven'
      } else {
        outcome = 'broken'
      }
    }
    out.push({ day, week, outcome, due, done })
  }
  return out
}

/** Days on which the member had something due and completed all of it. */
export function qualifyingDayCount(
  memberId: MemberId,
  instances: readonly ChoreInstance[],
  upToDay: DayKey,
  settings: StreakSettings = DEFAULT_STREAK_SETTINGS,
): number {
  return streakDays(memberId, instances, upToDay, settings).filter(d => d.outcome === 'qualifying')
    .length
}

/**
 * A member's streak state as of `upToDay`. Derived from the instance rows on
 * every call; nothing is stored, so nothing can drift.
 *
 * `lastQualifyingDay` is the most recent qualifying day at all, which may
 * predate a break — it answers "when did they last do everything", not "when
 * did the current run start".
 */
export function computeStreak(
  memberId: MemberId,
  instances: readonly ChoreInstance[],
  upToDay: DayKey,
  settings: StreakSettings = DEFAULT_STREAK_SETTINGS,
): StreakState {
  const grace = Math.max(0, Math.trunc(settings.graceDaysPerWeek ?? 1))
  const days = streakDays(memberId, instances, upToDay, settings)
  const week = officeWeekOf(upToDay, settings.weekStartDow)

  let run = 0
  let longest = 0
  let forgivenThisWeek = 0
  let lastQualifyingDay: DayKey | null = null

  for (const d of days) {
    if (d.outcome === 'qualifying') {
      run++
      if (run > longest) longest = run
      lastQualifyingDay = d.day
    } else if (d.outcome === 'broken') {
      run = 0
    }
    if (d.outcome === 'forgiven' && d.week === week) forgivenThisWeek++
  }

  return {
    memberId,
    currentStreak: run,
    longestStreak: longest,
    graceRemaining: Math.max(0, grace - forgivenThisWeek),
    week,
    lastQualifyingDay,
  }
}

/**
 * The day-rollover job, as a pure function: settle every open instance whose day
 * has passed into `missed` or `graced`, according to the same derived grace
 * ledger `computeStreak` uses.
 *
 * Statuses are reconciled in both directions so that devices converge: a day the
 * ledger forgives ends up `graced` even if another device wrote `missed`, and
 * vice versa. Instances due today or later are never touched, and `done` is
 * never overwritten.
 *
 * Pass the member's *full* history for the weeks involved. A silently truncated
 * read (docs/02 §8, landmine 1) would produce a wrong grace ledger.
 */
export function settleOverdue(
  instances: readonly ChoreInstance[],
  today: DayKey,
  settings: StreakSettings = DEFAULT_STREAK_SETTINGS,
): ChoreInstance[] {
  const members = new Set<MemberId>()
  for (const inst of instances) members.add(inst.assigneeId)

  const verdict = new Map<string, 'forgiven' | 'broken'>()
  for (const member of members) {
    for (const d of streakDays(member, instances, today, settings)) {
      if (d.outcome === 'forgiven' || d.outcome === 'broken') {
        verdict.set(`${member} ${d.day}`, d.outcome)
      }
    }
  }

  return instances.map(inst => {
    if (inst.status === 'done') return inst
    if (daysBetween(inst.dueDay, today) <= 0) return inst // not yet overdue
    const v = verdict.get(`${inst.assigneeId} ${inst.dueDay}`)
    if (v === undefined) return inst
    const want: ChoreStatus = v === 'forgiven' ? 'graced' : 'missed'
    return inst.status === want ? inst : { ...inst, status: want }
  })
}

// ---------------------------------------------------------------------------
// 4. Swaps (docs/01 §3.2)
// ---------------------------------------------------------------------------

export type ChoreError =
  | 'not-assignee'
  | 'instance-not-open'
  | 'instance-mismatch'
  | 'self-swap'
  | 'duplicate-swap'
  | 'swap-not-pending'
  | 'not-target'
  | 'broadcast-not-declinable'
  | 'not-requester'
  | 'ineligible-acceptor'
  | 'no-acceptances'

export type ChoreResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: ChoreError }

const ok = <T>(value: T): ChoreResult<T> => ({ ok: true, value })
const fail = <T>(error: ChoreError): ChoreResult<T> => ({ ok: false, error })

/** Open = still someone's outstanding work. Only open instances can be swapped. */
const isOpen = (inst: ChoreInstance): boolean =>
  inst.status === 'pending' || inst.status === 'swapped'

export interface RequestSwapInput {
  readonly instance: ChoreInstance
  readonly fromMemberId: MemberId
  /** Null broadcasts to everyone eligible; the first to accept takes it. */
  readonly toMemberId: MemberId | null
  readonly reason?: string | null | undefined
  readonly now: number
  /** Caller-minted (uuidv7 with a trusted clock) so this stays pure. */
  readonly id: SwapId
  /** Swaps already recorded against this instance, to reject a double request. */
  readonly existingSwaps?: readonly ChoreSwap[] | undefined
}

/** Raise a swap request. Does not touch the instance — nothing moves until acceptance. */
export function requestSwap(input: RequestSwapInput): ChoreResult<ChoreSwap> {
  const { instance, fromMemberId, toMemberId } = input
  if (instance.assigneeId !== fromMemberId) return fail('not-assignee')
  if (!isOpen(instance)) return fail('instance-not-open')
  if (toMemberId !== null && toMemberId === fromMemberId) return fail('self-swap')
  const existing = input.existingSwaps ?? []
  if (existing.some(s => s.instanceId === instance.id && s.status === 'pending')) {
    return fail('duplicate-swap')
  }
  return ok({
    id: input.id,
    instanceId: instance.id,
    fromMemberId,
    toMemberId,
    status: 'pending',
    reason: input.reason ?? null,
    createdAt: input.now,
    resolvedAt: null,
    resolvedBy: null,
  })
}

export interface AcceptSwapInput {
  readonly swap: ChoreSwap
  readonly instance: ChoreInstance
  readonly acceptorId: MemberId
  readonly now: number
  /** Optional eligibility filter (e.g. the definition's rotation pool). */
  readonly eligible?: readonly MemberId[] | undefined
}

/** Why this member may not take this swap, or null if they may. */
function acceptanceProblem(
  swap: ChoreSwap,
  instance: ChoreInstance,
  acceptorId: MemberId,
  eligible: readonly MemberId[] | undefined,
): ChoreError | null {
  if (swap.instanceId !== instance.id) return 'instance-mismatch'
  if (swap.status !== 'pending') return 'swap-not-pending'
  if (!isOpen(instance)) return 'instance-not-open'
  // The request is only good while the requester still holds the chore. A pool
  // change (`reassignPendingFrom`) can move a pending instance out from under an
  // outstanding request; without this guard the stale request would hand away a
  // chore that now belongs to somebody who never asked for cover, and fairness
  // would credit `swappedAway` to the wrong member.
  if (instance.assigneeId !== swap.fromMemberId) return 'not-assignee'
  if (acceptorId === swap.fromMemberId) return 'self-swap'
  if (swap.toMemberId !== null && swap.toMemberId !== acceptorId) return 'not-target'
  if (eligible !== undefined && !eligible.includes(acceptorId)) return 'ineligible-acceptor'
  return null
}

/**
 * Accept a swap. The instance moves to the acceptor and becomes `swapped`
 * (docs/01 §3.2).
 *
 * A broadcast swap is taken by the first acceptor: once the swap is `accepted`
 * a second acceptance fails with `swap-not-pending`, which is exactly the
 * "second acceptance must be rejected" rule. For two devices that accepted
 * while both were offline, see `resolveSwapRace`.
 */
export function acceptSwap(
  input: AcceptSwapInput,
): ChoreResult<{ swap: ChoreSwap; instance: ChoreInstance }> {
  const { swap, instance, acceptorId, now } = input
  const problem = acceptanceProblem(swap, instance, acceptorId, input.eligible)
  if (problem !== null) return fail(problem)
  return ok({
    swap: { ...swap, status: 'accepted', resolvedAt: now, resolvedBy: acceptorId },
    instance: { ...instance, assigneeId: acceptorId, status: 'swapped' },
  })
}

export interface DeclineSwapInput {
  readonly swap: ChoreSwap
  readonly declinedBy: MemberId
  readonly now: number
}

/**
 * Decline a *targeted* swap. A broadcast has no one to decline it — you decline
 * it by not accepting — so declining one is rejected rather than silently
 * killing everyone else's chance to take it. The requester withdraws their own
 * broadcast with `cancelSwap`.
 */
export function declineSwap(input: DeclineSwapInput): ChoreResult<ChoreSwap> {
  const { swap, declinedBy, now } = input
  if (swap.status !== 'pending') return fail('swap-not-pending')
  if (swap.toMemberId === null) return fail('broadcast-not-declinable')
  if (swap.toMemberId !== declinedBy) return fail('not-target')
  return ok({ ...swap, status: 'declined', resolvedAt: now, resolvedBy: declinedBy })
}

export interface CancelSwapInput {
  readonly swap: ChoreSwap
  readonly cancelledBy: MemberId
  readonly now: number
}

/** The requester withdraws their own pending request. */
export function cancelSwap(input: CancelSwapInput): ChoreResult<ChoreSwap> {
  const { swap, cancelledBy, now } = input
  if (swap.status !== 'pending') return fail('swap-not-pending')
  if (swap.fromMemberId !== cancelledBy) return fail('not-requester')
  return ok({ ...swap, status: 'cancelled', resolvedAt: now, resolvedBy: cancelledBy })
}

export interface SwapAcceptance {
  readonly memberId: MemberId
  /** Corrected UTC ms from the accepting device's TrustedClock. */
  readonly at: number
}

/**
 * First acceptance wins, with a deterministic tie-break.
 *
 * Two devices can both accept a broadcast while offline. Sorting by (at,
 * memberId) means every device replaying the same set picks the same winner
 * without asking the server, so the outcome does not depend on sync order.
 */
export function firstAcceptance(attempts: readonly SwapAcceptance[]): SwapAcceptance | null {
  let best: SwapAcceptance | null = null
  for (const a of attempts) {
    if (best === null || a.at < best.at || (a.at === best.at && a.memberId < best.memberId)) {
      best = a
    }
  }
  return best
}

export interface ResolveSwapRaceInput {
  readonly swap: ChoreSwap
  readonly instance: ChoreInstance
  readonly attempts: readonly SwapAcceptance[]
  readonly eligible?: readonly MemberId[] | undefined
}

/**
 * Resolve concurrent acceptances of one swap: discard the attempts that were
 * never valid, then apply the earliest remaining one. Permutation-invariant, so
 * two devices that received the attempts in different orders still agree.
 */
export function resolveSwapRace(
  input: ResolveSwapRaceInput,
): ChoreResult<{ swap: ChoreSwap; instance: ChoreInstance; winner: SwapAcceptance }> {
  const { swap, instance } = input
  const valid = input.attempts.filter(
    a => acceptanceProblem(swap, instance, a.memberId, input.eligible) === null,
  )
  const winner = firstAcceptance(valid)
  if (winner === null) {
    // Report why *deterministically*. Using `attempts[0]` here would make the
    // error depend on the order the attempts happened to sync in — two devices
    // holding the same set would disagree about the reason — so the
    // representative failure is the attempt that would have won had it been
    // valid, picked by the same (at, memberId) order as the winner itself.
    const probe = firstAcceptance(input.attempts)
    if (probe === null) return fail('no-acceptances')
    return fail(acceptanceProblem(swap, instance, probe.memberId, input.eligible) ?? 'no-acceptances')
  }
  const applied = acceptSwap({
    swap,
    instance,
    acceptorId: winner.memberId,
    now: winner.at,
    ...(input.eligible === undefined ? {} : { eligible: input.eligible }),
  })
  if (!applied.ok) return fail(applied.error)
  return ok({ ...applied.value, winner })
}

// ---------------------------------------------------------------------------
// 5. Escalation (docs/01 §3.4) — gentle, never red
// ---------------------------------------------------------------------------

/**
 * 0 on time, 1 at one day overdue, 2 at two or more.
 *
 * Completed and forgiven work never escalates. A `missed` instance still does:
 * the point of level 2 is a private nudge to get it finished, and a chore that
 * has been written off as missed but is still undone is exactly the case that
 * needs it.
 */
export function escalationFor(
  instance: Pick<ChoreInstance, 'status' | 'dueDay'>,
  today: DayKey,
): 0 | 1 | 2 {
  if (instance.status === 'done' || instance.status === 'graced') return 0
  const overdue = daysBetween(instance.dueDay, today)
  if (overdue <= 0) return 0
  return overdue === 1 ? 1 : 2
}

/** Recompute `escalationLevel` on every instance. Unchanged rows keep identity. */
export function escalateAll(
  instances: readonly ChoreInstance[],
  today: DayKey,
): ChoreInstance[] {
  return instances.map(inst => {
    const level = escalationFor(inst, today)
    return level === inst.escalationLevel ? inst : { ...inst, escalationLevel: level }
  })
}

export interface EscalationPresentation {
  readonly level: 0 | 1 | 2
  /** Never `'red'`. Chores stay gentle at every level (docs/01 §3.4). */
  readonly colour: 'normal' | 'amber'
  /** Level 2 pushes to the assignee's phone, privately. */
  readonly privatePush: boolean
  /** Always false: overdue chores are never named in large type on the wall. */
  readonly publiclyAttributedByName: false
}

/**
 * How a level renders. Encoded as data so "chores never go red and are never
 * publicly attributed by name" is a testable property rather than a comment.
 */
export function escalationPresentation(level: 0 | 1 | 2): EscalationPresentation {
  return {
    level,
    colour: level === 0 ? 'normal' : 'amber',
    privatePush: level === 2,
    publiclyAttributedByName: false,
  }
}

// ---------------------------------------------------------------------------
// 6. Fairness
// ---------------------------------------------------------------------------

/**
 * Per-member counts across the given instances and swaps.
 *
 *  - `assigned`     — instances *currently* assigned to them (post-swap).
 *  - `completed`    — done instances credited to whoever actually finished them.
 *  - `missed` / `graced` — charged to the current assignee.
 *  - `swappedAway`  — accepted swaps they requested.
 *  - `swappedIn`    — accepted swaps they took on.
 *
 * A habitual swapper is therefore visible without being punished: swapping away
 * costs nothing in `missed`, but it shows up in `swappedAway` (docs/01 §3.2).
 * Pass `members` to guarantee a row for someone with no activity at all.
 */
export function fairnessStats(
  instances: readonly ChoreInstance[],
  swaps: readonly ChoreSwap[] = [],
  members: readonly MemberId[] = [],
): FairnessStat[] {
  const stats = new Map<MemberId, FairnessStat>()
  const row = (id: MemberId): FairnessStat => {
    const found = stats.get(id)
    if (found !== undefined) return found
    const created: FairnessStat = {
      memberId: id,
      assigned: 0,
      completed: 0,
      missed: 0,
      graced: 0,
      swappedAway: 0,
      swappedIn: 0,
    }
    stats.set(id, created)
    return created
  }

  for (const id of members) row(id)

  for (const inst of instances) {
    const owner = row(inst.assigneeId)
    owner.assigned++
    if (inst.status === 'done') row(inst.completedBy ?? inst.assigneeId).completed++
    else if (inst.status === 'missed') owner.missed++
    else if (inst.status === 'graced') owner.graced++
  }

  for (const swap of swaps) {
    if (swap.status !== 'accepted') continue
    row(swap.fromMemberId).swappedAway++
    const acceptor = swap.resolvedBy ?? swap.toMemberId
    if (acceptor !== null) row(acceptor).swappedIn++
  }

  return [...stats.values()].sort((a, b) =>
    a.memberId < b.memberId ? -1 : a.memberId > b.memberId ? 1 : 0,
  )
}
